import React, { useState } from 'react';
import {
  Modal,
  Form,
  Input,
  Select,
  Button,
  Space,
  Table,
  Tag,
  Checkbox,
  Divider,
  Typography,
  Card,
} from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import { useCreateQualityPlanFromWorkOrder } from '../../hooks/useQuality';
import { useBulkCreateQualityPlanItems } from '../../hooks/useQualityPlanItem';
import { useInspectionPointLibrary } from '../../hooks/useInspectionPointLibrary';
import { DepartmentSelect } from '@/components/finance/DepartmentSelect';

const { TextArea } = Input;
const { Text, Title } = Typography;
const { Option } = Select;

interface CreateQualityPlanDialogProps {
  visible: boolean;
  workOrderId: string;
  workOrderNumber?: string;
  projectName?: string;
  onClose: () => void;
  onSuccess: () => void;
}

interface InspectionItem {
  key: string;
  inspection_type: 'Hold Point' | 'Witness Point' | 'Review Point' | 'Standard';
  description: string;
  acceptance_criteria?: string;
  witness_required: boolean;
  witness_party?: string;
  mandatory: boolean;
}

export const CreateQualityPlanDialog: React.FC<CreateQualityPlanDialogProps> = ({
  visible,
  workOrderId,
  workOrderNumber,
  projectName,
  onClose,
  onSuccess,
}) => {
  const [form] = Form.useForm();
  const [inspectionItems, setInspectionItems] = useState<InspectionItem[]>([]);
  const [submitType, setSubmitType] = useState<'draft' | 'submit'>('draft');

  const createQualityPlan = useCreateQualityPlanFromWorkOrder();
  const bulkCreateItems = useBulkCreateQualityPlanItems();
  const { data: inspectionLibrary = [] } = useInspectionPointLibrary({ is_active: true });

  const handleAddItem = () => {
    const newItem: InspectionItem = {
      key: `temp-${Date.now()}`,
      inspection_type: 'Standard',
      description: '',
      acceptance_criteria: '',
      witness_required: false,
      mandatory: true,
    };
    setInspectionItems([...inspectionItems, newItem]);
  };

  const handleDeleteItem = (key: string) => {
    setInspectionItems(inspectionItems.filter((item) => item.key !== key));
  };

  const handleImportFromLibrary = (selectedIds: string[]) => {
    const selectedPoints = inspectionLibrary.filter((point) => selectedIds.includes(point.id));
    const newItems: InspectionItem[] = selectedPoints.map((point) => ({
      key: `lib-${point.id}`,
      inspection_type: 'Standard',
      description: point.name + (point.description ? ` - ${point.description}` : ''),
      acceptance_criteria: point.standard_reference || '',
      witness_required: false,
      mandatory: true,
    }));
    setInspectionItems([...inspectionItems, ...newItems]);
  };

  const handleUpdateItem = (key: string, field: keyof InspectionItem, value: any) => {
    setInspectionItems(
      inspectionItems.map((item) =>
        item.key === key ? { ...item, [field]: value } : item
      )
    );
  };

  const handleSubmit = async (values: any) => {
    try {
      // Create Quality Plan
      const qualityPlan = await createQualityPlan.mutateAsync({
        workOrderId,
        data: values,
      });

      // Create inspection items if any
      if (inspectionItems.length > 0) {
        const itemsToCreate = inspectionItems.map((item, index) => ({
          sequence_number: index + 1,
          display_order: index + 1,
          inspection_type: item.inspection_type,
          description: item.description,
          acceptance_criteria: item.acceptance_criteria,
          witness_required: item.witness_required,
          witness_party: item.witness_party,
          mandatory: item.mandatory,
          status: 'Pending' as const,
          level: 1,
          can_skip: false,
        }));

        await bulkCreateItems.mutateAsync({
          planId: qualityPlan.id,
          items: itemsToCreate,
        });
      }

      // Submit for approval if requested
      if (submitType === 'submit' && inspectionItems.length > 0) {
        // Note: This would require another API call to submit
        // We'll handle this in the detail page for now
      }

      onSuccess();
      handleClose();
    } catch (error) {
      console.error('Error creating quality plan:', error);
    }
  };

  const handleClose = () => {
    form.resetFields();
    setInspectionItems([]);
    setSubmitType('draft');
    onClose();
  };

  const columns = [
    {
      title: 'Type',
      dataIndex: 'inspection_type',
      width: 150,
      render: (type: string, record: InspectionItem) => (
        <Select
          value={type}
          onChange={(value) => handleUpdateItem(record.key, 'inspection_type', value)}
          style={{ width: '100%' }}
          size="small"
        >
          <Option value="Standard">Standard</Option>
          <Option value="Review Point">Review Point</Option>
          <Option value="Witness Point">Witness Point</Option>
          <Option value="Hold Point">Hold Point</Option>
        </Select>
      ),
    },
    {
      title: 'Description',
      dataIndex: 'description',
      render: (text: string, record: InspectionItem) => (
        <Input
          value={text}
          onChange={(e) => handleUpdateItem(record.key, 'description', e.target.value)}
          placeholder="Enter inspection description"
          size="small"
        />
      ),
    },
    {
      title: 'Acceptance Criteria',
      dataIndex: 'acceptance_criteria',
      render: (text: string, record: InspectionItem) => (
        <Input
          value={text}
          onChange={(e) => handleUpdateItem(record.key, 'acceptance_criteria', e.target.value)}
          placeholder="Optional"
          size="small"
        />
      ),
    },
    {
      title: 'Witness',
      dataIndex: 'witness_required',
      width: 100,
      render: (checked: boolean, record: InspectionItem) => (
        <Checkbox
          checked={checked}
          onChange={(e) => handleUpdateItem(record.key, 'witness_required', e.target.checked)}
        />
      ),
    },
    {
      title: 'Actions',
      width: 80,
      render: (_: any, record: InspectionItem) => (
        <Button
          type="text"
          danger
          size="small"
          icon={<DeleteOutlined />}
          onClick={() => handleDeleteItem(record.key)}
        />
      ),
    },
  ];

  return (
    <Modal
      title={`Create Quality Plan - ${workOrderNumber || 'Work Order'}`}
      open={visible}
      onCancel={handleClose}
      width={1000}
      footer={null}
      destroyOnClose
    >
      <Card size="small" style={{ marginBottom: 16, backgroundColor: '#f5f5f5' }}>
        <Space direction="vertical" size="small">
          <Text strong>Work Order: {workOrderNumber}</Text>
          {projectName && <Text>Project: {projectName}</Text>}
        </Space>
      </Card>

      <Form form={form} layout="vertical" onFinish={handleSubmit}>
        <Form.Item
          name="department_id"
          label="Department"
          rules={[{ required: true, message: 'Department is required' }]}
        >
          <DepartmentSelect />
        </Form.Item>

        <Divider>Inspection Points</Divider>

        <Space style={{ marginBottom: 16 }}>
          <Button type="dashed" icon={<PlusOutlined />} onClick={handleAddItem}>
            Add Inspection Point
          </Button>
          <Select
            mode="multiple"
            placeholder="Import from Inspection Library"
            style={{ width: 300 }}
            onChange={handleImportFromLibrary}
            optionFilterProp="children"
            showSearch
          >
            {inspectionLibrary.map((point) => (
              <Option key={point.id} value={point.id}>
                {point.code} - {point.name}
              </Option>
            ))}
          </Select>
        </Space>

        <Table
          dataSource={inspectionItems}
          columns={columns}
          pagination={false}
          size="small"
          locale={{ emptyText: 'No inspection points added. Add at least one to submit for approval.' }}
        />

        <Divider />

        <Space style={{ marginTop: 16, float: 'right' }}>
          <Button onClick={handleClose}>Cancel</Button>
          <Button
            type="default"
            onClick={() => {
              setSubmitType('draft');
              form.submit();
            }}
            loading={createQualityPlan.isPending || bulkCreateItems.isPending}
          >
            Save as Draft
          </Button>
          <Button
            type="primary"
            onClick={() => {
              if (inspectionItems.length === 0) {
                Modal.warning({
                  title: 'No Inspection Points',
                  content: 'Please add at least one inspection point before submitting for approval.',
                });
                return;
              }
              setSubmitType('submit');
              form.submit();
            }}
            loading={createQualityPlan.isPending || bulkCreateItems.isPending}
            disabled={inspectionItems.length === 0}
          >
            Create & Submit for Approval
          </Button>
        </Space>
      </Form>
    </Modal>
  );
};
